import React, {useCallback, useId, useMemo, useRef, useState} from 'react'
import {Box, Flex, useSlotRecipe, Button} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import Slider from '../../../../../leap/components/cms/slider'
import Asset from '../../../../../leap/components/cms/asset'
import {galleryRecipe} from './index.recipe'
import WishlistToggle from '../../../../../components/wishlist-toggle'
import useXleapGalleryImages from '../../../../../leap/hooks/useXleapGalleryImages'
import ZoomImage from '../../../../../leap/components/zoomImage/zoomImage'
import ThumbsSlider from './thumbs'
import StackableImage from '../../../../../leap/components/stackable-image'
import useStackedImages from '../../../../../leap/hooks/useStackedImages'
import LoadingContainer from '../../../../../leap/components/loading-container'
import {getFileNameFromUrl} from '../../../../../utils/url'

const SLIDE_WIDTHS = {base: '90vw', s: '45vw', m: '50vw', l: '40vw'}

const Slide = ({asset, hotspots, onHotspotClick, activePid, styles}) => {
    const videoRef = React.useRef(null)
    const [isZoomed, setIsZoomed] = useState(false)
    const handleZoomChange = useCallback((zoomed) => setIsZoomed(zoomed), [])

    const imageUrl = asset.disBaseLink || asset.link
    const slideHotspots = useMemo(
        () => (imageUrl ? hotspots?.[getFileNameFromUrl(imageUrl)] : null),
        [imageUrl, hotspots]
    )

    if (asset.isLoading) {
        return (
            <LoadingContainer.Root height="100%" width="100%">
                <LoadingContainer.Status>
                    <LoadingContainer.Spinner />
                </LoadingContainer.Status>
            </LoadingContainer.Root>
        )
    }

    const isZoomEnabled = asset.type === 'image' || asset.type === 'stacked'

    const itemNode = (
        <Box aspectRatio={1}>
            {asset.type === 'stacked' ? (
                <StackableImage alt={asset.alt} images={asset.images} widths={SLIDE_WIDTHS} />
            ) : (
                <Asset
                    asset={asset}
                    videoRef={videoRef}
                    objectFit="cover"
                    widths={SLIDE_WIDTHS}
                    variant={'cockpitGallery'}
                />
            )}
        </Box>
    )

    return (
        <Box position="relative">
            {isZoomEnabled ? (
                <ZoomImage enabled onZoomChange={handleZoomChange} imageName={asset.alt}>
                    {itemNode}
                </ZoomImage>
            ) : (
                itemNode
            )}
            {!isZoomed &&
                slideHotspots?.map((hotspot) => {
                    const isActive = hotspot.pid === activePid
                    return (
                        <Button
                            unstyled
                            key={hotspot.pid}
                            data-testid="hotspot"
                            aria-label={hotspot.label}
                            onClick={() => onHotspotClick?.(hotspot)}
                            data-selected={isActive || undefined}
                            css={[
                                styles.hotspotButton,
                                {'--hotspot-x': `${hotspot.x}%`, '--hotspot-y': `${hotspot.y}%`}
                            ]}
                        />
                    )
                })}
        </Box>
    )
}

if (process.env.NODE_ENV !== 'production') {
    Slide.displayName = 'Slide'
}

Slide.propTypes = {
    asset: PropTypes.object,
    hotspots: PropTypes.object,
    onHotspotClick: PropTypes.func,
    activePid: PropTypes.string,
    styles: PropTypes.object
}

const Gallery = ({
    product,
    derivedProduct,
    isMasterLoading,
    isVariantLoading,
    hotspots,
    onHotspotClick,
    activePid
}) => {
    const mainSliderId = useId()

    const galleryRef = useRef(null)
    const [selectedIndex, setSelectedIndex] = useState(0)

    const recipe = useSlotRecipe({recipe: galleryRecipe})

    const styles = useMemo(() => recipe(), [recipe])
    const imageStacked = product?.c_imageStacked
    const stackableProducts = useMemo(
        () => [
            ...(product?.setProducts || []),
            ...(product?.setAccessories || []).filter((item) => item.isSetProductSelected)
        ],
        [product]
    )

    const nextStackedImage = useStackedImages({
        imageStacked,
        products: stackableProducts,
        enabled: Boolean(
            imageStacked && stackableProducts?.length && !isMasterLoading && !isVariantLoading
        )
    })

    const stackedImage = useMemo(
        () =>
            nextStackedImage?.images?.length > 0
                ? nextStackedImage
                : isMasterLoading || isVariantLoading
                ? {
                      isLoading: true
                  }
                : null,
        [nextStackedImage, isMasterLoading, isVariantLoading]
    )

    const galleryItems = useXleapGalleryImages(product, derivedProduct, stackedImage, {
        // opt-in per product set - otherwise the gallery shows only the set own images
        enrichWithMainSetProductImages: Boolean(product?.c_displayMainSetProductImages)
    })

    // the item count is part of the key so the slider resets when items are added after first
    // paint, for example the main set product images arriving with the referenced set products.
    // the reset drops the active slide back to the first one - the referenced set products are
    // fetched right after the initial render, while the gallery is still on its first item
    const sliderKey = useMemo(
        () =>
            [
                product?.setProducts?.length
                    ? product.setProducts.map((sp) => sp.id).join('-')
                    : product.id,
                galleryItems.length
            ].join('-'),
        [product, galleryItems]
    )

    return (
        <Flex {...styles.mainContainer}>
            <Box {...styles.thumbsContainer}>
                <ThumbsSlider
                    items={galleryItems}
                    selectedIndex={selectedIndex}
                    onSelect={(i) => galleryRef.current?.setCurrentIndex(i, {behavior: 'smooth'})}
                    onPrev={() => galleryRef.current?.prev()}
                    onNext={() => galleryRef.current?.next()}
                ></ThumbsSlider>
            </Box>
            <Box {...styles.galleryContainer}>
                <Box pointerEvents="auto" {...styles.wishlistButtonContainer}>
                    <WishlistToggle product={product} variant="cockpitGallery" />
                </Box>
                <Slider
                    ref={galleryRef}
                    navigationButtonSize="sm"
                    variant="cockpitGallery"
                    onIndexChange={setSelectedIndex}
                    sliderId={mainSliderId}
                    key={sliderKey}
                >
                    {galleryItems.map((item, index) => (
                        <Slide
                            product={product}
                            asset={item}
                            key={index}
                            hotspots={hotspots}
                            onHotspotClick={onHotspotClick}
                            activePid={activePid}
                            styles={styles}
                        />
                    ))}
                </Slider>
            </Box>
        </Flex>
    )
}

if (process.env.NODE_ENV !== 'production') {
    Gallery.displayName = 'Gallery'
}

Gallery.propTypes = {
    product: PropTypes.object,
    derivedProduct: PropTypes.object,
    isMasterLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    hotspots: PropTypes.object,
    onHotspotClick: PropTypes.func,
    activePid: PropTypes.string
}

export default Gallery
